// Authentication Context
// Provides global authentication state and methods
import React, { createContext, useContext, useState, useEffect } from 'react';
import { authService } from '../services';
import { getCurrentPort, getDashboardTypeFromPort, shouldRedirectUser, getExpectedDashboardForRole, getPortForDashboard } from '../utils/portUtils';
import { getLocalStorage } from '../utils/browserCheck.js';
import { isTokenExpired, handleTokenExpiry } from '../utils/authUtils.js';
import { API_CONFIG } from '../config/api.js';

// Create the context
const AuthContext = createContext();

// Helper function to check if user is hospital user
const isHospitalUserRole = (role) => {
  return role === 'HOSPITAL_USER' || role === 'HOSPITAL';
};

// Helper function to check if user is fleet user
const isFleetUserRole = (role) => {
  return role === 'TENANT_ADMIN' || role === 'TENANT_USER';
};

// Helper function to check if user needs profile fetch
const needsProfileFetch = (userData) => {
  if (!userData?.role) return false;
  return isHospitalUserRole(userData.role) || isFleetUserRole(userData.role);
};

// Helper function to handle redirect check
const handleRedirectCheck = (userData, dashboardType, authService, setUser, setIsAuthenticated) => {
  const expectedDashboard = getExpectedDashboardForRole(userData.role);
  const shouldRedirect = shouldRedirectUser(userData.role, dashboardType);

  console.log('🔍 [AuthContext] shouldRedirectUser check:', {
    userRole: userData.role,
    dashboardType,
    shouldRedirect,
    expectedDashboard,
    currentPort: getCurrentPort()
  });

  if (shouldRedirect) {
    console.error('❌ [AuthContext] Clearing auth due to redirection requirement');
    authService.clearAuth();
    setUser(null);
    setIsAuthenticated(false);
    return true;
  }
  return false;
};

// Helper function to fetch profile based on user type
const fetchUserProfile = async (isHospitalUser, isFleetUser, authService) => {
  if (isHospitalUser) {
    return await authService.hospitalGetProfile();
  }
  if (isFleetUser) {
    return await authService.fleetGetProfile();
  }
  return null;
};

// Helper function to merge fleet profile data
const mergeFleetProfileData = (userData, profileData) => {
  const merged = {
    ...userData,
    ...profileData,
    role: userData.role
  };
  // Always prefer a non-empty city: login response stores operator.city at root level (userData.city)
  // The profile API may return city: "" — don't let that overwrite a valid city
  merged.city = profileData?.city || userData?.city || userData?.operator?.city || '';
  merged.state = profileData?.state || userData?.state || userData?.operator?.state || '';
  return merged;
};

// Helper function to extract user from profile
const extractUserFromProfile = (profile, userData) => {
  return profile.data.user || profile.data || userData;
};

// Helper function to process profile data
const processProfileData = (profile, userData, isFleetUser, setUser) => {
  if (!profile?.data) {
    setUser(userData);
    return;
  }

  if (isFleetUser) {
    const mergedUserData = mergeFleetProfileData(userData, profile.data);
    console.log('✅ [AuthContext] Fleet profile merged, setting authenticated', mergedUserData);
    setUser(mergedUserData);
    // Persist merged data (with city) back to localStorage
    try {
      const storage = getLocalStorage();
      if (storage) {
        storage.setItem(API_CONFIG.STORAGE_KEYS.USER_DATA, JSON.stringify(mergedUserData));
        console.log('✅ [AuthContext] Merged user data saved to localStorage');
      }
    } catch (e) {
      console.warn('Failed to persist merged user data:', e);
    }
  } else {
    setUser(extractUserFromProfile(profile, userData));
  }
};

// Helper function to fetch and process profile
const fetchAndProcessProfile = async (userData, authService, setUser) => {
  const isHospitalUser = isHospitalUserRole(userData.role);
  const isFleetUser = isFleetUserRole(userData.role);

  console.log(`🔄 [AuthContext] Fetching complete profile for ${isFleetUser ? 'fleet' : 'hospital'} user`);

  try {
    const profile = await fetchUserProfile(isHospitalUser, isFleetUser, authService);
    processProfileData(profile, userData, isFleetUser, setUser);
  } catch (error) {
    console.warn('Profile verification failed, but keeping user authenticated:', error);
    setUser(userData);
  }
};

// Helper function to handle invalid auth state
const handleInvalidAuthState = (authService, setUser, setIsAuthenticated) => {
  console.error('❌ [AuthContext] User is authenticated but no user data found in localStorage');
  console.error('❌ [AuthContext] Raw localStorage data:');
  const storage = getLocalStorage();
  if (storage) {
    console.error('❌ [AuthContext] JWT Token exists:', !!storage.getItem('aps_jwt_token'));
    console.error('❌ [AuthContext] User data:', storage.getItem('aps_user_data'));
  }

  authService.clearAuth();
  setUser(null);
  setIsAuthenticated(false);
};

// Helper function to process authenticated user
const processAuthenticatedUser = async (userData, dashboardType, authService, setUser, setIsAuthenticated) => {
  if (handleRedirectCheck(userData, dashboardType, authService, setUser, setIsAuthenticated)) {
    return;
  }

  if (needsProfileFetch(userData)) {
    await fetchAndProcessProfile(userData, authService, setUser);
  } else {
    setUser(userData);
  }

  setIsAuthenticated(true);
};

// Helper function to clear auth state
const clearAuthState = (authService, setUser, setIsAuthenticated) => {
  authService.clearAuth();
  setUser(null);
  setIsAuthenticated(false);
};

// AuthProvider component
export const AuthProvider = ({ children }) => {
  // State
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Initialize authentication state
  useEffect(() => {
    initializeAuth();
  }, []);

  const initializeAuth = async () => {
    try {
      const dashboardType = getDashboardTypeFromPort();

      if (!authService.isAuthenticated()) {
        setIsLoading(false);
        return;
      }

      // Check if token is expired BEFORE setting loading state
      // This prevents showing the loading screen if token is expired
      const storage = getLocalStorage();
      if (storage) {
        const token = storage.getItem(API_CONFIG.STORAGE_KEYS.JWT_TOKEN);
        if (token && isTokenExpired(token)) {
          console.warn('🚨 [AuthContext] Token is expired, redirecting to login immediately');
          // Clear auth and redirect without showing loading screen
          await handleTokenExpiry('Token expired during initialization');
          return;
        }
      }

      // Only set loading state after confirming token is not expired
      setIsLoading(true);

      const userData = authService.getUser();
      console.log('🔍 [AuthContext] Retrieved user data from localStorage:', userData);
      console.log('🔍 [AuthContext] User role:', userData?.role);

      if (!userData) {
        handleInvalidAuthState(authService, setUser, setIsAuthenticated);
        setIsLoading(false);
        return;
      }

      await processAuthenticatedUser(userData, dashboardType, authService, setUser, setIsAuthenticated);
    } catch (error) {
      console.error('Auth initialization error:', error);
      // If error is due to token expiry (401), handle it
      if (error.response?.status === 401 || error.message?.includes('Token expired')) {
        console.warn('🚨 [AuthContext] Token expired during profile fetch, redirecting to login');
        setIsLoading(false);
        await handleTokenExpiry('Token expired during profile fetch');
        return;
      }
      clearAuthState(authService, setUser, setIsAuthenticated);
    } finally {
      setIsLoading(false);
    }
  };

  // Login function
  const login = async (credentials) => {
    try {
      setIsLoading(true);
      setError(null);

      const currentPort = getCurrentPort();
      const dashboardType = getDashboardTypeFromPort();

      const result = await authService.login(credentials);

      if (result.success && result.data.user) {
        const userRole = result.data.user.role;
        const expectedDashboard = getExpectedDashboardForRole(userRole);
        const shouldRedirect = shouldRedirectUser(userRole, dashboardType);

        console.log('🔍 [AuthContext] Login shouldRedirectUser check:', {
          userRole,
          dashboardType,
          shouldRedirect,
          expectedDashboard,
          currentPort
        });

        if (shouldRedirect) {
          // Clear the auth that was just stored
          authService.clearAuth();
          throw new Error(`Access denied. ${userRole} users should login from the ${expectedDashboard} dashboard (Port ${getPortForDashboard(expectedDashboard)}).`);
        }

        // Check if this is a fleet or hospital user that needs profile data
        const userData = result.data.user;
        const isHospitalUser = userData.role === 'HOSPITAL_USER' || userData.role === 'HOSPITAL';
        const isFleetUser = userData.role === 'TENANT_ADMIN' || userData.role === 'TENANT_USER';

        // For fleet and hospital users, fetch complete profile BEFORE setting authenticated
        if (isFleetUser || isHospitalUser) {
          console.log(`🔄 [AuthContext] Login - Fetching complete profile for ${isFleetUser ? 'fleet' : 'hospital'} user`);

          try {
            let profile;
            if (isHospitalUser) {
              profile = await authService.hospitalGetProfile();
            } else if (isFleetUser) {
              profile = await authService.fleetGetProfile();
            }

            // Update user data with complete profile BEFORE setting authenticated
            if (profile?.data) {
              if (isFleetUser) {
                const mergedUserData = {
                  ...userData, // Keep original user data with role
                  ...profile.data, // Add fleet profile data
                  role: userData.role // Ensure role is preserved
                };
                console.log('✅ [AuthContext] Login - Fleet profile merged, setting authenticated');
                setUser(mergedUserData);
              } else {
                setUser(profile.data.user || profile.data || userData);
              }
            } else {
              // Profile fetch failed but had successful response, use original data
              setUser(userData);
            }
          } catch (error) {
            // Profile verification failed, but keep user authenticated with login data
            console.warn('Profile verification failed during login, but keeping user authenticated:', error);
            setUser(userData);
          }
        } else {
          // For other users (super admin, etc.), set immediately
          setUser(userData);
        }

        setIsAuthenticated(true);
        return result;
      } else {
        throw new Error(result.message || 'Login failed');
      }
    } catch (error) {
      setError(error.message || 'Login failed');
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // Login with OTP function
  const loginWithOtp = async (loginData) => {
    try {
      setIsLoading(true);
      setError(null);

      const currentPort = getCurrentPort();
      const dashboardType = getDashboardTypeFromPort();

      // Use specific endpoint based on dashboard type
      const result = dashboardType === 'hospital'
        ? await authService.hospitalCompleteLogin(loginData)
        : dashboardType === 'fleet'
          ? await authService.fleetCompleteLogin(loginData)
          : await authService.completeLogin(loginData);

      if (result.success && result.data.user) {
        // First-time login: Password reset is mandatory before issuing session tokens
        if (result.data.requiresPasswordReset) {
          console.log('🔒 [AuthContext] Password reset required on first login. Skipping profile fetch and auth state.');
          return result;
        }

        const userRole = result.data.user.role;
        const expectedDashboard = getExpectedDashboardForRole(userRole);
        const shouldRedirect = shouldRedirectUser(userRole, dashboardType);

        console.log('🔍 [AuthContext] LoginWithOtp shouldRedirectUser check:', {
          userRole,
          dashboardType,
          shouldRedirect,
          expectedDashboard,
          currentPort
        });

        if (shouldRedirect) {
          // Clear the auth that was just stored
          authService.clearAuth();
          throw new Error(`Access denied. ${userRole} users should login from the ${expectedDashboard} dashboard (Port ${getPortForDashboard(expectedDashboard)}).`);
        }

        // Check if this is a fleet or hospital user that needs profile data
        const userData = result.data.user;
        const isHospitalUser = userData.role === 'HOSPITAL_USER' || userData.role === 'HOSPITAL';
        const isFleetUser = userData.role === 'TENANT_ADMIN' || userData.role === 'TENANT_USER';

        // For fleet and hospital users, fetch complete profile BEFORE setting authenticated
        if (isFleetUser || isHospitalUser) {
          console.log(`🔄 [AuthContext] LoginWithOtp - Fetching complete profile for ${isFleetUser ? 'fleet' : 'hospital'} user`);

          try {
            let profile;
            if (isHospitalUser) {
              profile = await authService.hospitalGetProfile();
            } else if (isFleetUser) {
              profile = await authService.fleetGetProfile();
            }

            // Update user data with complete profile BEFORE setting authenticated
            if (profile?.data) {
              if (isFleetUser) {
                const mergedUserData = {
                  ...userData, // Keep original user data with role
                  ...profile.data, // Add fleet profile data
                  role: userData.role // Ensure role is preserved
                };
                console.log('✅ [AuthContext] LoginWithOtp - Fleet profile merged, setting authenticated');
                setUser(mergedUserData);
              } else {
                setUser(profile.data.user || profile.data || userData);
              }
            } else {
              // Profile fetch failed but had successful response, use original data
              setUser(userData);
            }
          } catch (error) {
            // Profile verification failed, but keep user authenticated with login data
            console.warn('Profile verification failed during login, but keeping user authenticated:', error);
            setUser(userData);
          }
        } else {
          // For other users (super admin, etc.), set immediately
          setUser(userData);
        }

        setIsAuthenticated(true);
        return result;
      } else {
        throw new Error(result.message || 'Login failed');
      }
    } catch (error) {
      setError(error.message || 'Login failed');
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // Logout function
  const logout = async () => {
    try {
      // Check user type and use appropriate logout endpoint
      const userData = authService.getUser();
      const isHospitalUser = userData && (userData.role === 'HOSPITAL' || userData.role === 'hospital');
      const isFleetUser = userData && (userData.role === 'TENANT_ADMIN' || userData.role === 'TENANT_USER' || userData.role === 'fleet');

      if (isHospitalUser) {
        console.log('🏥 Logging out hospital user');
        await authService.hospitalLogout();
      } else if (isFleetUser) {
        console.log('🚛 Logging out fleet user');
        await authService.fleetLogout();
      } else {
        console.log('🔑 Logging out admin user');
        await authService.logout();
      }
    } finally {
      setUser(null);
      setIsAuthenticated(false);
      setError(null);
    }
  };

  // Clear error function
  const clearError = () => {
    setError(null);
  };

  // Set user directly (used after successful login to update state immediately)
  const setUserDirectly = (userData) => {
    if (userData) {
      setUser(userData);
      setIsAuthenticated(true);
      setIsLoading(false);
    }
  };

  const value = {
    user,
    isAuthenticated,
    isLoading,
    error,
    login,
    loginWithOtp,
    logout,
    clearError,
    initializeAuth,
    setUserDirectly
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
